import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Modal, Button } from '../../shared/sharedComponent';
import api from '../../services/api';
import './ExternalLayout.css';
import { toast } from 'react-toastify';
import nsaLogo from '../../assets/nsa-logo.png';

const PASSWORD_BANNER_DISMISS_KEY = 'nsa_pw_reminder_dismissed_at';
const IDENT_MODAL_DISMISS_KEY = 'nsa_ident_modal_dismissed_at';

// Icons shared between the desktop nav and the mobile dropdown — same
// paths as the equivalent internal-side nav items, for visual consistency.
const IconSurveys = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
  </svg>
);
const IconHistory = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
  </svg>
);
const IconSettings = ({ width = 16, height = 16 }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" width={width} height={height}>
    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
  </svg>
);
const IconLogout = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
    <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
  </svg>
);

const ExternalLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase();
  const [pwBannerDismissed, setPwBannerDismissed] = useState(
    () => sessionStorage.getItem(PASSWORD_BANNER_DISMISS_KEY) === 'true'
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dismissPwBanner = () => {
    sessionStorage.setItem(PASSWORD_BANNER_DISMISS_KEY, 'true');
    setPwBannerDismissed(true);
  };
  const showPwBanner = user?.password_change_recommended && !pwBannerDismissed;

  // Prompt providers to complete their establishment's identification
  // details if any of the establishments they're primarily responsible
  // for haven't filled it in yet — dismissible per session, but reappears
  // on the next login until it's actually completed (survey submission
  // is blocked server-side until then regardless).
  const [incompleteEsts, setIncompleteEsts] = useState([]);
  const [identModalDismissed, setIdentModalDismissed] = useState(
    () => sessionStorage.getItem(IDENT_MODAL_DISMISS_KEY) === 'true'
  );
  useEffect(() => {
    api.get('/external/profile')
      .then(({ data }) => {
        const incomplete = (data.data?.establishments || [])
          .filter((e) => e.dp_role === 'primary' && !e.identification_complete);
        setIncompleteEsts(incomplete);
      })
      .catch(() => {});
  }, []);
  const dismissIdentModal = () => {
    sessionStorage.setItem(IDENT_MODAL_DISMISS_KEY, 'true');
    setIdentModalDismissed(true);
  };
  // Don't nag on the Settings page itself — that's exactly where the
  // "Update Now" button sends them, and the modal would otherwise block
  // the very form it's telling them to fill in.
  const showIdentModal = incompleteEsts.length > 0 && !identModalDismissed
    && !location.pathname.startsWith('/external/settings');

  const handleLogout = async () => {
    await logout();
    navigate('/provider-login');
  };

  const goTo = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  return (
    <div className="ext-layout">
      <header className="ext-header">
        <div className="ext-header__brand">
          <img src={nsaLogo} alt="Namibia Statistics Agency" className="ext-header__logo" />
          <div>
            <span className="ext-header__title">NSA Survey Portal</span>
            <span className="ext-header__subtitle">Respondent Interface</span>
          </div>
        </div>

        <nav className="ext-header__nav">
          <NavLink to="/external/dashboard" end className={({ isActive }) => `ext-nav-link ${isActive ? 'ext-nav-link--active' : ''}`}>
            <IconSurveys /> Active Surveys
          </NavLink>
          <NavLink to="/external/surveys/past" className={({ isActive }) => `ext-nav-link ${isActive ? 'ext-nav-link--active' : ''}`}>
            <IconHistory /> Past Surveys
          </NavLink>
        </nav>

        <div className="ext-header__user">
          {/* Profile pill — clickable → settings; the gear icon signals that explicitly */}
          <button className="ext-header__profile" onClick={() => navigate('/external/settings')} title="Account settings">
            <div className="ext-header__profile-info">
              <span className="ext-header__profile-name">
                {user?.firstName} {user?.lastName}
              </span>
              <span className="ext-header__profile-role">Respondent</span>
            </div>
            <span className="ext-header__profile-settings-icon"><IconSettings /></span>
            <div className="ext-header__avatar">
              {user?.profile_picture
                ? <img src={user.profile_picture} alt={initials} className="ext-header__avatar-img" />
                : <span>{initials}</span>
              }
            </div>
          </button>

          {/* Sign out */}
          <button className="ext-header__logout" onClick={handleLogout} title="Sign out">
            <IconLogout />
          </button>
        </div>

        {/* Mobile-only: avatar + hamburger, everything else collapses into the dropdown */}
        <div className="ext-header__mobile-controls">
          <button className="ext-header__avatar ext-header__avatar--mobile" onClick={() => goTo('/external/settings')} aria-label="Account settings">
            {user?.profile_picture
              ? <img src={user.profile_picture} alt={initials} className="ext-header__avatar-img" />
              : <span>{initials}</span>
            }
          </button>
          <button
            className="ext-header__hamburger"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            ) : (
              <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"/>
              </svg>
            )}
          </button>
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="ext-mobile-menu">
          {/* Identity — who's signed in, not itself a link */}
          <div className="ext-mobile-menu__user">
            <div className="ext-header__avatar">
              {user?.profile_picture
                ? <img src={user.profile_picture} alt={initials} className="ext-header__avatar-img" />
                : <span>{initials}</span>
              }
            </div>
            <div className="ext-mobile-menu__user-info">
              <span className="ext-mobile-menu__user-name">{user?.firstName} {user?.lastName}</span>
              <span className="ext-mobile-menu__user-role">Respondent</span>
            </div>
          </div>
          <div className="ext-mobile-menu__divider" />
          <button className="ext-mobile-menu__link" onClick={() => goTo('/external/dashboard')}>
            <IconSurveys /> Active Surveys
          </button>
          <button className="ext-mobile-menu__link" onClick={() => goTo('/external/surveys/past')}>
            <IconHistory /> Past Surveys
          </button>
          <div className="ext-mobile-menu__divider" />
          <button className="ext-mobile-menu__link" onClick={() => goTo('/external/settings')}>
            <IconSettings /> Account Settings
          </button>
          <button className="ext-mobile-menu__link ext-mobile-menu__link--danger" onClick={() => { setMobileMenuOpen(false); handleLogout(); }}>
            <IconLogout /> Sign Out
          </button>
        </div>
      )}

      {showPwBanner && (
        <div className="pw-reminder-banner">
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
          </svg>
          <span>
            It's been {user.password_age_days} days since you last changed your password. For account security, please update it.
          </span>
          <button className="pw-reminder-banner__action" onClick={() => navigate('/external/settings?tab=password')}>
            Change Password
          </button>
          <button className="pw-reminder-banner__dismiss" onClick={dismissPwBanner} aria-label="Dismiss">
            <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>
      )}

      <Modal
        open={showIdentModal}
        onClose={dismissIdentModal}
        title="Complete Your Establishment Details"
        footer={
          <>
            <Button variant="secondary" onClick={dismissIdentModal}>Remind Me Later</Button>
            <Button variant="primary" onClick={() => {
              dismissIdentModal();
              navigate(`/external/settings?tab=identification&est=${incompleteEsts[0]?.establishment_id || ''}`);
            }}>
              Update Now →
            </Button>
          </>
        }
      >
        <p style={{ fontSize: '0.9rem', color: 'var(--gray-700, #374151)', lineHeight: 1.6, margin: 0 }}>
          Before you can submit a survey, NSA needs a few identification details for{' '}
          <strong>{incompleteEsts.map((e) => e.establishment_name).join(', ')}</strong> — address,
          contact info and main business activity. You can fill these in under{' '}
          <strong>Account Settings → Update Identification Info</strong>.
        </p>
      </Modal>

      <main className="ext-layout__main">
        <Outlet />
      </main>

      <footer className="ext-footer">
        <p>Namibia Statistics Agency · Economics Department · PO Box  2133, Windhoek</p>
        <p>Information collected under the Statistics Act no. 9 of 2011 — strictly confidential.</p>
      </footer>
    </div>
  );
};

export default ExternalLayout;